import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';
import { CATEGORY_OPTIONS, LANGUAGE_OPTIONS } from '../../components/pages/seoOptions';

/**
 * SEO text — the copy shown on one area/sport/language landing page.
 *
 * Everything here is **public the moment it is saved**: no deploy, no cache to
 * wait for. The old form says so in its success message, which is after the fact;
 * this page says it before, next to the button.
 *
 * The three keys — area slug, category, language — are what the public page looks
 * up. Changing one of them on an existing row does not edit that page, it moves
 * the text to a different page and leaves the original with nothing, so they are
 * grouped and labelled as the key rather than mixed in with the copy.
 *
 * `H1Title` and `TopText` are the two fields the frontend reads for the heading
 * and the intro block; `ExtraText` is the long body at the bottom of the page.
 */

const EMPTY = {
  AreaSlug: '', Category: CATEGORY_OPTIONS[0].value, Language: LANGUAGE_OPTIONS[0].value,
  MetaTitle: '', MetaDescription: '', MetaKeywords: '', H1Title: '', TopText: '', ExtraText: ''
};

const SeoTextInternal = ({ api }) => {
  const { seoTextId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();

  const isNew = !seoTextId;

  const [row, setRow] = useState(isNew ? {} : null);
  const [form, setForm] = useState(isNew ? EMPTY : null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const toForm = data => ({
    AreaSlug: data.areaSlug || '',
    Category: String(data.category ?? CATEGORY_OPTIONS[0].value),
    Language: String(data.language ?? LANGUAGE_OPTIONS[0].value),
    MetaTitle: data.metaTitle || '',
    MetaDescription: data.metaDescription || '',
    MetaKeywords: data.metaKeywords || '',
    H1Title: data.h1Title || '',
    TopText: data.topText || '',
    ExtraText: data.extraText || ''
  });

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await api.seoTexts.getSeoText(seoTextId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setRow(data);
      setForm(toForm(data));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, seoTextId, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/seo-texts">← All SEO texts</BackToList>
        <div className="next-note next-note--error">
          Could not load this SEO text.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!form) return <div className="next-page next-muted">Loading…</div>;

  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));

  const pristine = isNew ? EMPTY : toForm(row);
  const dirty = Object.keys(form).some(key => form[key] !== pristine[key]);
  const keyChanged = !isNew && ['AreaSlug', 'Category', 'Language'].some(key => form[key] !== pristine[key]);

  const invalid = !form.AreaSlug.trim();

  const save = async event => {
    event.preventDefault();
    if (invalid || (!dirty && !isNew) || busy) return;

    const payload = {
      AreaSlug: form.AreaSlug.trim(),
      Category: parseInt(form.Category, 10),
      Language: parseInt(form.Language, 10),
      MetaTitle: form.MetaTitle.trim() || null,
      MetaDescription: form.MetaDescription.trim() || null,
      MetaKeywords: form.MetaKeywords.trim() || null,
      H1Title: form.H1Title.trim() || null,
      TopText: form.TopText.trim() || null,
      ExtraText: form.ExtraText.trim() || null
    };

    setBusy(true);
    setNote(null);
    try {
      const result = isNew
        ? await api.seoTexts.createSeoText(payload)
        : await api.seoTexts.updateSeoText({ ...payload, Id: parseInt(seoTextId, 10) });

      if (result?.success === false) {
        setNote({ tone: 'error', text: result.message || 'The text was not saved.' });
        return;
      }

      if (isNew && result?.id) {
        navigate(`/next/seo-text/${result.id}`);
        return;
      }
      await load();
      setNote({ tone: 'info', text: 'Saved — live on the site now.' });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.payload?.message || error?.response?.data?.message
          || error?.message || 'Unable to save the SEO text.'
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = () => ask({
    kind: 'danger',
    title: 'Delete this SEO text?',
    context: `Delete · ${form.AreaSlug}`,
    body: 'The landing page for this area, sport and language goes back to the templated copy. It changes on the public site immediately.',
    warning: 'The text is not recoverable from the admin.',
    confirmLabel: 'Delete text',
    onConfirm: async () => {
      try {
        await api.seoTexts.deleteSeoText(seoTextId);
      } catch (error) {
        setNote({ tone: 'error', text: error?.message || 'Unable to delete it.' });
        throw error;
      }
      navigate('/next/seo-texts');
    }
  });

  const text = (name, label, { rows, hint } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      {rows
        ? (
          <textarea
            className="next-input next-textarea"
            rows={rows}
            value={form[name]}
            onChange={event => set(name, event.target.value)}
          />
        )
        : (
          <input
            className="next-input"
            value={form[name]}
            onChange={event => set(name, event.target.value)}
          />
        )}
      {hint && <span className="next-form__hint">{hint}</span>}
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/seo-texts">← All SEO texts</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{isNew ? 'New SEO text' : (form.AreaSlug || 'SEO text')}</h1>
          <span className="next-pill next-pill--neutral">
            {(CATEGORY_OPTIONS.find(option => option.value === form.Category) || {}).label || form.Category}
          </span>
          <span className="next-pill next-pill--info">
            {(LANGUAGE_OPTIONS.find(option => option.value === form.Language) || {}).label || form.Language}
          </span>
        </div>
        {!isNew && <span className="next-detail__id">#{row.id}</span>}
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <form onSubmit={save}>
            <section className="next-card">
              <div className="next-card__head">
                <h2>Which page</h2>
                <span className="next-muted">area, sport and language identify the landing page</span>
              </div>
              <div className="next-form__grid">
                {text('AreaSlug', 'Area slug', { hint: 'e.g. aarhus — as it appears in the URL.' })}

                <label className="next-filter">
                  <span className="next-filter__label">Category</span>
                  <select
                    className="next-input"
                    value={form.Category}
                    onChange={event => set('Category', event.target.value)}
                  >
                    {CATEGORY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="next-filter">
                  <span className="next-filter__label">Language</span>
                  <select
                    className="next-input"
                    value={form.Language}
                    onChange={event => set('Language', event.target.value)}
                  >
                    {LANGUAGE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              {keyChanged && (
                <div className="next-form__grid">
                  <span className="next-form__error">
                    Changing the area, sport or language moves this text to a different
                    landing page — the one it is on now goes back to templated copy.
                  </span>
                </div>
              )}
            </section>

            <section className="next-card">
              <div className="next-card__head"><h2>On the page</h2></div>
              <div className="next-form__grid">
                {text('H1Title', 'H1 title', { hint: 'Replaces the generated heading.' })}
                {text('MetaTitle', 'Meta title')}
              </div>
              <div className="next-form__row">
                {text('TopText', 'Top text', { rows: 4, hint: 'Intro block under the heading.' })}
              </div>
              <div className="next-form__row">
                {text('MetaDescription', 'Meta description', { rows: 3 })}
              </div>
              <div className="next-form__row">
                {text('MetaKeywords', 'Meta keywords')}
              </div>
              <div className="next-form__row">
                {text('ExtraText', 'Body', { rows: 10, hint: 'The long text at the bottom of the page.' })}
              </div>

              <div className="next-form__actions">
                <button
                  type="submit"
                  className="next-btn next-btn--primary"
                  disabled={invalid || (!dirty && !isNew) || busy}
                  title={invalid ? 'An area slug is required' : (!dirty && !isNew ? 'Nothing has changed' : undefined)}
                >
                  {busy ? 'Saving…' : (isNew ? 'Create text' : 'Save changes')}
                </button>
                {!isNew && (
                  <button
                    type="button"
                    className="next-btn"
                    disabled={!dirty || busy}
                    onClick={() => setForm(toForm(row))}
                  >
                    Discard
                  </button>
                )}
                <span className="next-muted">Saving publishes it immediately.</span>
              </div>
            </section>
          </form>
        </div>

        <aside className="next-detail__side">
          {!isNew && (
            <section className="next-card">
              <div className="next-card__head"><h2>Actions</h2></div>
              <div className="next-actionlist">
                <button
                  type="button"
                  className="next-actionlist__item next-actionlist__item--danger"
                  onClick={remove}
                >
                  Delete text
                </button>
              </div>
            </section>
          )}

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                form.AreaSlug && {
                  label: 'Other texts for this area',
                  hint: form.AreaSlug,
                  to: `/next/seo-texts?areaSlug=${encodeURIComponent(form.AreaSlug)}`
                },
                form.AreaSlug && {
                  label: 'The area',
                  to: `/next/areas?slug=${encodeURIComponent(form.AreaSlug)}`
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

SeoTextInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const SeoText = withApi(SeoTextInternal);

SeoText.requiresLogin = true;

export default SeoText;
